import { Selo, juntarClasses } from '../../componentes/ui';
import {
  formatarDataIso,
  situacaoDoPrazo,
  type CardResumo,
  type TagDoCrm,
} from '../../servicos/crm';

/**
 * O card como aparece no quadro e no calendário: tags, razão social e o rodapé
 * com prazo, comentários e quem acompanha.
 *
 * Só apresentação. Arrastar é do Kanban, que embrulha este componente; abrir o
 * detalhe é do título, um botão de verdade — alcançável por teclado sem
 * depender do arrastar.
 */
export function CardDoPipeline(props: {
  readonly card: CardResumo;
  readonly tags: ReadonlyMap<string, TagDoCrm>;
  readonly aoAbrir?: () => void;
  /** O card levantado, sob o cursor: sombra mais forte, sem clique. */
  readonly flutuando?: boolean;
  /** No calendário: sem tags e sem rodapé, só o nome. */
  readonly compacto?: boolean;
}): JSX.Element {
  const { card } = props;
  const tags = card.tagIds.flatMap((id) => {
    const tag = props.tags.get(id);
    return tag === undefined ? [] : [tag];
  });
  const nome = card.nomeFantasia?.trim() || card.razaoSocial;
  const prazo = card.dataExecucao === null ? null : situacaoDoPrazo(card.dataExecucao);

  return (
    <article
      className={juntarClasses(
        'flex flex-col gap-2 rounded-card border border-borda bg-superficie-elevada text-left shadow-cartao',
        props.compacto === true ? 'px-2 py-1.5' : 'p-3',
        // No calendário não há rodapé: o prazo aparece na borda.
        props.compacto === true && prazo === 'vencida' && 'border-l-4 border-l-erro',
        props.compacto === true && prazo === 'hoje' && 'border-l-4 border-l-atencao',
        props.flutuando === true && 'rotate-1 shadow-lg ring-2 ring-acento-ciano/40',
      )}
    >
      {props.compacto !== true && tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag) => (
            <Selo key={tag.id} tom={tag.cor} tamanho="pequeno" semPonto>
              {tag.nome}
            </Selo>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={props.aoAbrir}
        // O arrastar escuta o card inteiro; o título só abre.
        className={juntarClasses(
          'line-clamp-2 text-left font-medium text-conteudo-primario underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano',
          props.compacto === true ? 'text-xs' : 'text-sm',
        )}
        title={card.razaoSocial}
      >
        {nome}
      </button>

      {props.compacto !== true && (
        <div className="flex items-center justify-between gap-2 text-xs text-conteudo-sutil">
          <span className="flex items-center gap-3">
            {card.dataExecucao !== null && <Prazo iso={card.dataExecucao} />}
            {card.atividadesPendentes > 0 && (
              <IndicadorDeAtividades
                pendentes={card.atividadesPendentes}
                proximoPrazo={card.proximoPrazo}
              />
            )}
            {card.comentarios > 0 && (
              <span
                className="flex items-center gap-1"
                title={`${card.comentarios} ${card.comentarios === 1 ? 'comentário' : 'comentários'}`}
              >
                <IconeComentario />
                {card.comentarios}
              </span>
            )}
          </span>
          {card.participantes.length > 0 && (
            <span className="flex -space-x-1.5">
              {card.participantes.slice(0, 3).map((p) => (
                <span
                  key={p.accountId}
                  title={p.nome}
                  className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-superficie-elevada bg-acento-indigo/15 text-[0.6rem] font-semibold text-acento-indigo"
                >
                  {iniciais(p.nome)}
                </span>
              ))}
            </span>
          )}
        </div>
      )}
    </article>
  );
}

/** Atividades em aberto; vermelho se a mais próxima já venceu, laranja se é hoje. */
function IndicadorDeAtividades(props: {
  readonly pendentes: number;
  readonly proximoPrazo: string | null;
}): JSX.Element {
  const situacao = props.proximoPrazo === null ? null : situacaoDoPrazo(props.proximoPrazo);
  return (
    <span
      className={juntarClasses(
        'flex items-center gap-1',
        situacao === 'vencida' && 'font-medium text-erro',
        situacao === 'hoje' && 'font-medium text-atencao',
      )}
      title={`${props.pendentes} ${props.pendentes === 1 ? 'atividade em aberto' : 'atividades em aberto'}${
        props.proximoPrazo === null ? '' : ` — próxima em ${formatarDataIso(props.proximoPrazo)}`
      }${situacao === 'vencida' ? ' (atrasada)' : ''}`}
    >
      <IconeTarefa />
      {props.pendentes}
    </span>
  );
}

/** O prazo, pintado: vermelho vencido, laranja hoje, neutro adiante. */
function Prazo(props: { readonly iso: string }): JSX.Element {
  const situacao = situacaoDoPrazo(props.iso);
  return (
    <span
      className={juntarClasses(
        'flex items-center gap-1',
        situacao === 'vencida' && 'font-medium text-erro',
        situacao === 'hoje' && 'font-medium text-atencao',
      )}
      title={
        situacao === 'vencida'
          ? 'Data de execução vencida'
          : situacao === 'hoje'
            ? 'Data de execução é hoje'
            : 'Data de execução'
      }
    >
      <IconeCalendario />
      {formatarDataIso(props.iso).slice(0, 5)}
    </span>
  );
}

export function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  const primeira = partes[0]?.[0] ?? '';
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';
  return `${primeira}${ultima}`.toUpperCase();
}

function IconeCalendario(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M3 9h18M8 2v4M16 2v4" />
    </svg>
  );
}

function IconeComentario(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12Z" />
    </svg>
  );
}

function IconeTarefa(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </svg>
  );
}
